/* eslint-disable no-nested-ternary */
import React, { useState } from "react";
import MDBox from "components/MDBox";
import Modal from "@mui/material/Modal";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { Autocomplete, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import useYupValidationResolver from "utils/yup/useYupValidationResolver";
import * as Yup from "yup";
import { postAPICall, putAPICall } from "lib/callApi";
import { COMPANY_ENABLED_MODULE } from "lib/constants";
import { enqueueSnackbar } from "notistack";

function ModuleManagementModal({
  open,
  handleModalClose,
  isLoading,
  companiesList,
  setIsLoading,
  modulesList,
  editFormValues,
  getModulesData,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 400 ? "100%" : 400,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    boxShadow: 24,
    p: 4,
    outline: "none",
    borderRadius: "0.5rem",
  };

  const [initialValues] = useState({
    companyId: editFormValues?.FK_companies_id,
    moduleId: editFormValues?.FK_modules_id,
    moduleComponentId: editFormValues?.FK_modules_components_id,
    enabled: editFormValues ? editFormValues?.enabled || false : true,
  });

  const validationSchema = Yup.object({
    companyId: Yup.number().required("Company is required"),
    moduleId: Yup.number().required("Module is required"),
    enabled: Yup.boolean(),
    moduleComponentId: Yup.string().when("moduleId", {
      is: (value) =>
        modulesList?.find((moduleObject) => moduleObject?.id === value)?.ModulesComponents?.length,
      then: Yup.string().required("Module Component is required"),
      otherwise: Yup.string().nullable(),
    }),
  });

  const resolver = useYupValidationResolver(validationSchema);
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver,
    reValidateMode: "all",
    mode: "all",
    defaultValues: initialValues,
  });

  const watchModuleId = watch("moduleId");
  const modulesComponentsList = watchModuleId
    ? modulesList?.find((moduleObject) => moduleObject?.id === watchModuleId)?.ModulesComponents ||
      []
    : [];

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const result = editFormValues
        ? await putAPICall(`${COMPANY_ENABLED_MODULE}/${editFormValues.id}`, true, data)
        : await postAPICall(COMPANY_ENABLED_MODULE, true, data);
      if (result.data.success) {
        setIsLoading(false);
        reset();
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
        getModulesData();
        handleModalClose?.(true);
      } else {
        setIsLoading(false);
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result.data.message,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        handleModalClose();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <Typography variant="h5" mb={2}>
          {editFormValues ? "Edit Module" : "Create New Module"}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <MDBox
            sx={{
              "& .MuiFormHelperText-root": { color: "#d32f2f" },
            }}
          >
            <MDBox mb={2}>
              <Controller
                control={control}
                name="companyId"
                rules={{ required: true }}
                render={({ field: { value } }) => (
                  <Autocomplete
                    autoComplete
                    includeInputInList
                    id="companyId"
                    fullWidth
                    value={value ? companiesList.find((option) => option.id === value) : null}
                    onChange={(_, option) => setValue("companyId", option?.id)}
                    options={companiesList}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, val) => option?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Company"
                        error={errors?.companyId?.message}
                        helperText={errors?.companyId?.message}
                      />
                    )}
                    disabled={isLoading || !!editFormValues}
                  />
                )}
              />
            </MDBox>
            <MDBox mb={2}>
              <Controller
                control={control}
                name="moduleId"
                rules={{ required: true }}
                render={({ field: { value } }) => (
                  <Autocomplete
                    autoComplete
                    includeInputInList
                    id="moduleId"
                    fullWidth
                    value={value ? modulesList.find((option) => option.id === value) : null}
                    onChange={(_, option) => {
                      setValue("moduleId", option?.id);
                      setValue("moduleComponentId", "");
                    }}
                    options={modulesList}
                    getOptionLabel={(option) => option?.module_name || ""}
                    isOptionEqualToValue={(option, val) => option?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Module"
                        error={errors?.moduleId?.message}
                        helperText={errors?.moduleId?.message}
                      />
                    )}
                    disabled={isLoading || !!editFormValues}
                  />
                )}
              />
            </MDBox>
            <MDBox mb={2}>
              <Controller
                control={control}
                name="moduleComponentId"
                rules={{ required: true }}
                render={({ field: { value } }) => (
                  <Autocomplete
                    autoComplete
                    includeInputInList
                    id="moduleComponentId"
                    fullWidth
                    value={
                      value ? modulesComponentsList?.find((option) => option.id === value) : null
                    }
                    onChange={(_, option) => setValue("moduleComponentId", option?.id)}
                    options={modulesComponentsList}
                    getOptionLabel={(option) => option?.component_name || ""}
                    isOptionEqualToValue={(option, val) => option?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Module Component"
                        error={errors?.moduleComponentId?.message}
                        helperText={errors?.moduleComponentId?.message}
                      />
                    )}
                    disabled={isLoading || !!editFormValues}
                  />
                )}
              />
            </MDBox>
            <MDBox mb={2}>
              <Controller
                control={control}
                name="enabled"
                rules={{ required: true }}
                render={({ field: { value } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="enabled"
                        checked={value || false}
                        onChange={(e) => setValue("enabled", e.target.checked)}
                      />
                    }
                    label="Enabled"
                  />
                )}
              />
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton disabled={isLoading} color="primaryRed" fullWidth type="submit">
                {editFormValues ? "EDIT" : "CREATE"}
              </MDButton>
            </MDBox>
          </MDBox>
        </form>
      </MDBox>
    </Modal>
  );
}

export default ModuleManagementModal;
